import type {
  GradingAnswer,
  GradingDiffFactor,
  GradingFamily,
  GradingQuestion,
  GradingRole,
  GradingRoleCluster,
  GradingSpecialization,
} from '../grading/types';
import { formatPath, resolveRolePath } from '../grading/utils';

export type ScoringQuestionItem = {
  factor: GradingDiffFactor;
  question: GradingQuestion;
  questionIndexInFactor: number;
  questionsInFactor: number;
};

export function visibleAnswersForQuestion(
  answers: GradingAnswer[],
  questionId: number,
): GradingAnswer[] {
  return answers
    .filter((a) => a.question_id === questionId && a.is_visible !== false)
    .sort((a, b) => a.answer_score - b.answer_score || a.id - b.id);
}

export function matchRoleByScore(
  roles: GradingRole[],
  roleClusterId: number,
  specializationId: number | null,
  totalScore: number,
): GradingRole | undefined {
  const candidates = roles.filter((r) => {
    if (r.role_cluster_id !== roleClusterId) return false;
    if (specializationId && r.specialization_id && r.specialization_id !== specializationId) return false;
    if (r.min_score == null || r.max_score == null) return false;
    return totalScore >= r.min_score && totalScore <= r.max_score;
  });
  return candidates.sort((a, b) => b.grade_level - a.grade_level)[0];
}

export function buildRoleLabel(
  role: GradingRole,
  specs: GradingSpecialization[],
  roleClusters: GradingRoleCluster[],
  families: GradingFamily[],
): string {
  const { family, roleCluster, spec, role: r } = resolveRolePath(role, specs, roleClusters, families);
  const path = formatPath([family?.name, roleCluster?.name, spec?.name, r?.name]);
  return `${path} · G${role.grade_level}`;
}

export function questionsByFactor(
  roleClusterId: number,
  familyId: number,
  factors: GradingDiffFactor[],
  questions: GradingQuestion[],
): ScoringQuestionItem[] {
  const familyFactors = factors.filter(
    (f) => f.family_id === familyId && f.is_visible !== false,
  );
  const result: ScoringQuestionItem[] = [];
  familyFactors.forEach((factor) => {
    const factorQuestions = questions
      .filter(
        (q) => q.role_cluster_id === roleClusterId
          && q.diff_factor_id === factor.id
          && q.is_visible !== false,
      )
      .sort((a, b) => a.id - b.id);
    factorQuestions.forEach((question, index) => {
      result.push({
        factor,
        question,
        questionIndexInFactor: index + 1,
        questionsInFactor: factorQuestions.length,
      });
    });
  });
  return result;
}
